import { useEffect, useState, useMemo, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Spinner } from '../components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MapLocationButton } from '../components/MapLocationButton';
import { MapPin, Search, ExternalLink, Landmark, ScrollText } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const poiIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const bicIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-gold.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const createClusterIcon = (color) => (cluster) => {
  const count = cluster.getChildCount();
  const size = count >= 100 ? 50 : count >= 10 ? 40 : 30;
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px;box-shadow:0 4px 12px rgba(0,0,0,0.3);background-color:${color};">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size, true),
  });
};

export function TourismPage() {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('pois');
  
  const [pois, setPois] = useState([]);
  const [bic, setBic] = useState([]);
  const [loadingPois, setLoadingPois] = useState(true);
  const [loadingBic, setLoadingBic] = useState(false);
  const [search, setSearch] = useState('');
  const [poiTypeFilter, setPoiTypeFilter] = useState('all');
  const [bicCategoryFilter, setBicCategoryFilter] = useState('all');

  const t = {
    title: language === 'es' ? 'Puntos de Interés' : 'Points of Interest',
    subtitle: language === 'es' ? 'Lugares destacados de Tenerife' : 'Highlighted places in Tenerife',
    poisTab: language === 'es' ? 'Puntos de Interés' : 'Points of Interest',
    bicTab: language === 'es' ? 'Bienes de Interés Cultural' : 'Cultural Heritage',
    searchPlaceholder: language === 'es' ? 'Buscar por nombre o municipio...' : 'Search by name or municipality...',
    showing: language === 'es' ? 'Mostrando' : 'Showing',
    of: language === 'es' ? 'de' : 'of',
    results: language === 'es' ? 'resultados' : 'results',
    allTypes: language === 'es' ? 'Todos' : 'All',
    filterByType: language === 'es' ? 'Filtrar por tipo' : 'Filter by type',
    filterByCategory: language === 'es' ? 'Filtrar por categoría' : 'Filter by category',
    loading: language === 'es' ? 'Cargando...' : 'Loading...',
    openInGoogleMaps: language === 'es' ? 'Abrir en Google Maps' : 'Open in Google Maps',
    moreInfo: language === 'es' ? 'Más información' : 'More info',
  };

  // POI type colors
  const poiTypeColors = {
    'Patrimonio Geológico': '#ef4444',
    'Patrimonio Arquitectónico': '#8b5cf6', 
    'Patrimonio Biológico': '#22c55e',
    'Asentamientos': '#f97316',
    'Patrimonio Etnográfico': '#eab308',
    'Servicios y Facilidades': '#3b82f6',
    'Cultura y Arte': '#ec4899',
  };

  // BIC category colors
  const bicCategoryColors = {
    'Monumento': '#ef4444',
    'Conjunto Histórico': '#8b5cf6',
    'Sitio Histórico': '#22c55e',
    'Zona Arqueológica': '#f97316',
    'Bien de Interés Cultural': '#3b82f6',
  };

  // Fetch POIs on initial load
  const fetchPois = useCallback(async () => {
    setLoadingPois(true);
    try {
      const response = await axios.get(`${API}/tourism/points-of-interest`);
      setPois(response.data || []);
    } catch (error) {
      console.error('Error fetching POIs:', error);
    } finally {
      setLoadingPois(false);
    }
  }, []);

  useEffect(() => {
    fetchPois();
  }, [fetchPois]);

  // Fetch BIC when tab changes
  useEffect(() => {
    if (activeTab === 'bic' && bic.length === 0) {
      const fetchBic = async () => {
        setLoadingBic(true);
        try {
          const response = await axios.get(`${API}/tourism/cultural-heritage`);
          setBic(response.data || []);
        } catch (error) {
          console.error('Error fetching BIC:', error);
        } finally {
          setLoadingBic(false);
        }
      };
      fetchBic();
    }
  }, [activeTab, bic.length]);

  // Get unique POI types for filter
  const poiTypes = useMemo(() => {
    const types = new Set();
    pois.forEach(p => {
      if (p.tipo) types.add(p.tipo);
    });
    return Array.from(types).sort();
  }, [pois]);

  // Get unique BIC categories for filter
  const bicCategories = useMemo(() => {
    const categories = new Set();
    bic.forEach(b => {
      if (b.categoria) categories.add(b.categoria);
    });
    return Array.from(categories).sort();
  }, [bic]);

  // Filter POIs
  const filteredPois = useMemo(() => {
    let filtered = pois;
    
    if (poiTypeFilter !== 'all') {
      filtered = filtered.filter(item => item.tipo === poiTypeFilter);
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item =>
        (item.nombre || '').toLowerCase().includes(searchLower) ||
        (item.municipio || '').toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [pois, search, poiTypeFilter]);

  // Filter BIC
  const filteredBic = useMemo(() => {
    let filtered = bic;
    
    if (bicCategoryFilter !== 'all') {
      filtered = filtered.filter(item => item.categoria === bicCategoryFilter);
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item =>
        (item.nombre || '').toLowerCase().includes(searchLower) ||
        (item.municipio || '').toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [bic, search, bicCategoryFilter]);

  const poiMarkers = useMemo(() => filteredPois.filter(item => item.lat && item.lon), [filteredPois]);
  const bicMarkers = useMemo(() => filteredBic.filter(item => item.lat && item.lon), [filteredBic]);

  const centerPosition = [28.2916, -16.5291];

  const openInGoogleMaps = (lat, lon) => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lon}`, '_blank');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-[#22c55e]" />
          <span className="text-sm font-medium text-[#22c55e] uppercase tracking-wider">
            {language === 'es' ? 'Datos Abiertos' : 'Open Data'}
          </span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#252a34] font-['Outfit'] mb-2" data-testid="tourism-title">
          {t.title}
        </h1>
        <p className="text-lg text-[#252a34]/70">{t.subtitle}</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 h-12">
          <TabsTrigger value="pois" className="text-sm font-medium">
            <MapPin className="w-4 h-4 mr-2" />
            {t.poisTab}
          </TabsTrigger>
          <TabsTrigger value="bic" className="text-sm font-medium">
            <Landmark className="w-4 h-4 mr-2" />
            {t.bicTab}
          </TabsTrigger>
        </TabsList>

        {/* POIs Tab */}
        <TabsContent value="pois" className="space-y-4">
          {/* Stats */}
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-slate-200">
              <span className="text-2xl font-bold text-[#22c55e]">{pois.length}</span>
              <span className="text-gray-600 ml-2">{language === 'es' ? 'puntos' : 'points'}</span>
            </div>
          </div>

          {/* POI Type Filters */}
          {!loadingPois && (
            <div className="mb-4">
              <p className="text-sm text-[#252a34]/60 mb-2">{t.filterByType}:</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setPoiTypeFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    poiTypeFilter === 'all'
                      ? 'bg-[#22c55e] text-white'
                      : 'bg-white text-[#252a34] border border-slate-200 hover:border-[#22c55e]'
                  }`}
                >
                  {t.allTypes} ({pois.length})
                </button>
                {poiTypes.map((tipo) => {
                  const count = pois.filter(p => p.tipo === tipo).length;
                  const color = poiTypeColors[tipo] || '#6b7280';
                  const isActive = poiTypeFilter === tipo;
                  return (
                    <button
                      key={tipo}
                      onClick={() => setPoiTypeFilter(tipo)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isActive ? 'text-white shadow-md' : 'bg-white text-[#252a34] border border-slate-200 hover:shadow-sm'
                      }`}
                      style={isActive ? { backgroundColor: color } : {}}
                    >
                      {tipo} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search */}
          {!loadingPois && (
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#252a34]/40" />
                <Input
                  type="text"
                  placeholder={t.searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-12 bg-white border-slate-200"
                />
              </div>
              <div className="text-sm text-[#252a34]/60 flex items-center gap-2">
                {t.showing} <strong className="text-[#22c55e]">{filteredPois.length.toLocaleString()}</strong> {t.of} {pois.length.toLocaleString()} {t.results}
              </div>
            </div>
          )}

          {/* Map */}
          <Card className="overflow-hidden border-slate-200">
            <div className="h-[400px] md:h-[450px]">
              {loadingPois ? (
                <div className="flex flex-col items-center justify-center h-full bg-slate-50">
                  <Spinner size="lg" />
                  <p className="mt-4 text-[#252a34]/60 text-sm font-medium">{t.loading}</p>
                </div>
              ) : (
                <MapContainer center={centerPosition} zoom={9} style={{ height: '100%', width: '100%' }} className="z-0">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapLocationButton language={language} />
                  <MarkerClusterGroup chunkedLoading iconCreateFunction={createClusterIcon('#22c55e')} maxClusterRadius={50}>
                    {poiMarkers.map((item, idx) => (
                      <Marker key={idx} position={[item.lat, item.lon]} icon={poiIcon}>
                        <Popup>
                          <div className="font-['Outfit'] min-w-[250px] max-w-[300px]">
                            <h3 className="font-bold text-[#252a34] mb-1">{item.nombre}</h3>
                            {item.tipo && (
                              <Badge className="text-xs mb-2 text-white" style={{ backgroundColor: poiTypeColors[item.tipo] || '#6b7280' }}>
                                {item.tipo}
                              </Badge>
                            )}
                            {item.descripcion && <p className="text-sm text-[#252a34]/70 mb-2">{item.descripcion}</p>}
                            {item.municipio && <p className="text-xs text-[#252a34]/50 mb-2">📍 {item.municipio}</p>}
                            <button onClick={() => openInGoogleMaps(item.lat, item.lon)} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-2">
                              <ExternalLink className="w-3 h-3" /> {t.openInGoogleMaps}
                            </button>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MarkerClusterGroup>
                </MapContainer>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* BIC Tab */}
        <TabsContent value="bic" className="space-y-4">
          {/* Stats */}
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-slate-200">
              <span className="text-2xl font-bold text-amber-500">{bic.length}</span>
              <span className="text-gray-600 ml-2">{language === 'es' ? 'bienes' : 'sites'}</span>
            </div>
          </div>

          {/* BIC Category Filters */}
          {!loadingBic && bic.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-[#252a34]/60 mb-2">{t.filterByCategory}:</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setBicCategoryFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    bicCategoryFilter === 'all'
                      ? 'bg-amber-500 text-white'
                      : 'bg-white text-[#252a34] border border-slate-200 hover:border-amber-400'
                  }`}
                >
                  {t.allTypes} ({bic.length})
                </button>
                {bicCategories.map((cat) => {
                  const count = bic.filter(b => b.categoria === cat).length;
                  const color = bicCategoryColors[cat] || '#6b7280';
                  const isActive = bicCategoryFilter === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => setBicCategoryFilter(cat)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isActive ? 'text-white shadow-md' : 'bg-white text-[#252a34] border border-slate-200 hover:shadow-sm'
                      }`}
                      style={isActive ? { backgroundColor: color } : {}}
                    >
                      {cat} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search */}
          {!loadingBic && (
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#252a34]/40" />
                <Input
                  type="text"
                  placeholder={t.searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-12 bg-white border-slate-200"
                />
              </div>
              <div className="text-sm text-[#252a34]/60 flex items-center gap-2">
                {t.showing} <strong className="text-amber-500">{filteredBic.length.toLocaleString()}</strong> {t.of} {bic.length.toLocaleString()} {t.results}
              </div>
            </div>
          )}

          {/* Map */}
          <Card className="overflow-hidden border-slate-200">
            <div className="h-[400px] md:h-[450px]">
              {loadingBic ? (
                <div className="flex flex-col items-center justify-center h-full bg-slate-50">
                  <Spinner size="lg" />
                  <p className="mt-4 text-[#252a34]/60 text-sm font-medium">{t.loading}</p>
                </div>
              ) : (
                <MapContainer center={centerPosition} zoom={9} style={{ height: '100%', width: '100%' }} className="z-0">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapLocationButton language={language} />
                  <MarkerClusterGroup chunkedLoading iconCreateFunction={createClusterIcon('#f59e0b')} maxClusterRadius={50}>
                    {bicMarkers.map((item, idx) => (
                      <Marker key={idx} position={[item.lat, item.lon]} icon={bicIcon}>
                        <Popup>
                          <div className="font-['Outfit'] min-w-[250px] max-w-[300px]">
                            <h3 className="font-bold text-[#252a34] mb-1">{item.nombre}</h3>
                            {item.categoria && (
                              <Badge className="text-xs mb-2 text-white" style={{ backgroundColor: bicCategoryColors[item.categoria] || '#6b7280' }}>
                                {item.categoria}
                              </Badge>
                            )}
                            {item.municipio && <p className="text-xs text-[#252a34]/50 mb-2">📍 {item.municipio}</p>}
                            {item.boletin_url && (
                              <a href={item.boletin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mb-2">
                                <ScrollText className="w-3 h-3" /> {t.moreInfo}
                              </a>
                            )}
                            <button onClick={() => openInGoogleMaps(item.lat, item.lon)} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-2">
                              <ExternalLink className="w-3 h-3" /> {t.openInGoogleMaps}
                            </button>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MarkerClusterGroup>
                </MapContainer>
              )}
            </div>
          </Card>

          {/* List */}
          {!loadingBic && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredBic.slice(0, 30).map((item, idx) => (
                <Card key={idx} className="overflow-hidden border-slate-200 hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-[#252a34] mb-2">{item.nombre}</h3>
                    {item.categoria && (
                      <Badge className="text-xs mb-2 text-white" style={{ backgroundColor: bicCategoryColors[item.categoria] || '#6b7280' }}>
                        {item.categoria}
                      </Badge>
                    )}
                    {item.municipio && <p className="text-xs text-[#252a34]/50">📍 {item.municipio}</p>}
                    {item.lat && item.lon && (
                      <button onClick={() => openInGoogleMaps(item.lat, item.lon)} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-2">
                        <ExternalLink className="w-3 h-3" /> {t.openInGoogleMaps}
                      </button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
